import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone } from 'lucide-react';
import PageHero from '../components/PageHero';
import { Img } from '../components/Img';
import { IMG } from '../images';

const services = [
  {
    id: 'window-cleaning',
    title: 'High Rise Window & Facade Cleaning',
    description: 'Professional high-rise window and façade cleaning using advanced rope access techniques.',
    features: [
      'Internal and external window cleaning',
      'Facade washing for glass, aluminium, concrete, stone, ACP, cladding and louvers',
      'Soft wash and pure water cleaning for delicate surfaces',
      'Pressure washing for durable substrates (safe PSI settings)',
      'Post-construction final cleans: sticker removal, silicone smears, cement haze',
      'Stain treatments: mineral deposits, efflorescence, rust, mould',
      'Detailed cleaning of frames, sills, tracks, spider webs and bird droppings',
    ],
    image: IMG.bucket,
    alt: IMG.bucket.alt,
  },
  {
    id: 'remedial',
    title: 'Remedial Work & Building Maintenance at Height',
    description: 'Comprehensive remedial and maintenance tasks at height to extend building life and ensure safety.',
    features: [
      'Concrete repairs and rendering',
      'Caulking and sealing of joints, windows and façade elements',
      'Application of protective coatings and weatherproof sealants',
      'Minor façade repairs and leak investigations',
      'Small-scale painting and touch-ups in hard-to-reach areas',
      'Fast execution with minimal disruption',
    ],
    image: IMG.wash,
    alt: IMG.wash.alt,
  },
  {
    id: 'glazing',
    title: 'Glazing & Panel Installation at Height',
    description: 'Safe and precise glazing and panel installation services using rope access techniques.',
    features: [
      'Installation and replacement of glass panels on high-rise façades',
      'Curtain wall glazing repairs and adjustments',
      'Replacement of damaged façade panels and cladding',
      'Sealant replacement and perimeter sealing around windows',
      'Coordination with builders and façade engineers',
      'Minimal disruption to building operations',
    ],
    image: IMG.lift,
    alt: IMG.lift.alt,
  },
  {
    id: 'height-safety',
    title: 'Height Safety Systems – Installation & Inspection',
    description: 'Installation and inspection of height safety systems for compliance and worker protection.',
    features: [
      'Installation of anchor points and height safety systems',
      'Certification and periodic inspection of existing systems',
      'Height safety audits and compliance checks',
      'Recommendations for improving safe access',
      'Installation certified to Australian standards',
      'Industry best practices compliance',
    ],
    image: IMG.ledge,
    alt: IMG.ledge.alt,
  },
  {
    id: 'bird-control',
    title: 'Bird Pest Control & Façade Protection',
    description: 'Discreet and effective bird control solutions suitable for high-rise environments.',
    features: [
      'Bird proofing using netting, spikes and deterrent systems',
      'Cleaning and disinfection of affected areas',
      'Removal of nests and accumulated droppings (where permitted)',
      'Ongoing maintenance plans to keep façades clean',
      'Installation in difficult-to-reach locations',
      'Minimal visual impact on building aesthetics',
    ],
    image: IMG.cityview,
    alt: IMG.cityview.alt,
  },
  {
    id: 'emergency',
    title: 'Emergency Rope Access & Rapid Response',
    description: 'Quick and safe emergency rope access services for urgent situations.',
    features: [
      'Securing loose panels, glass or façade elements',
      'Urgent leak investigations and temporary repairs',
      'Storm damage response and risk mitigation',
      'On-site assessment and action plan within short timeframes',
      '24/7 emergency availability',
      'Immediate risk assessment and response',
    ],
    image: IMG.darkfacade,
    alt: IMG.darkfacade.alt,
  },
];

const steps = [
  { step: '01', title: 'Site Assessment', description: 'We review your building, access points and specific challenges' },
  { step: '02', title: 'Scope & Quote', description: 'We define the work, methods and safety measures, then provide a clear quote' },
  { step: '03', title: 'Safe Setup', description: 'Our team installs rope systems and safety equipment according to plan' },
  { step: '04', title: 'Execution', description: 'We complete the work efficiently, with minimal disruption to occupants' },
  { step: '05', title: 'Reporting', description: 'You receive a summary of work completed, with photos and recommendations' },
];

const ServiceBlock = ({ service, index, link, single }) => (
  <div className="service-detail reveal">
    <div style={{ order: index % 2 === 0 ? 1 : 2 }}>
      <h3 style={{ fontSize: '2.2rem', color: 'var(--navy-blue)', marginBottom: '1rem' }}>{single ? "What's Included" : service.title}</h3>
      {!single && <p style={{ color: 'var(--steel-gray)', fontSize: '1.1rem', marginBottom: '1.5rem' }}>{service.description}</p>}
      <ul className="service-list">
        {service.features.map((feature) => (
          <li key={feature}>
            <CheckCircle size={20} aria-hidden="true" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1.5rem' }}>
        <Link to="/contact#quote-form" className="btn-primary">Request Quote <ArrowRight size={18} aria-hidden="true" /></Link>
        {link && <Link to={`/services/${service.id}`} className="btn-outline">Service details</Link>}
      </div>
    </div>
    <div className="photo-frame" style={{ order: index % 2 === 0 ? 2 : 1 }}>
      <Img image={service.image} alt={service.alt} sizes="(max-width: 900px) 100vw, 600px" style={{ aspectRatio: '4 / 3' }} />
    </div>
  </div>
);

const Services = () => {
  const { serviceId } = useParams();
  const resolvedId = serviceId === 'facade-cleaning' ? 'window-cleaning' : serviceId;
  const current = services.find((s) => s.id === resolvedId);

  return (
    <div>
      {current ? (
        <PageHero
          image={IMG.hero}
          position="55% 50%"
          title={serviceId === 'facade-cleaning' ? 'Facade Cleaning' : current.title}
          subtitle={current.description}
          minHeight="56vh"
        >
          <div className="hero-buttons">
            <Link to="/contact#quote-form" className="btn-primary">Request a Quote <ArrowRight size={18} aria-hidden="true" /></Link>
            <a href="tel:0426920301" className="btn-secondary"><Phone size={18} aria-hidden="true" /> 0426 920 301</a>
          </div>
        </PageHero>
      ) : (
        <PageHero
          image={IMG.hero}
          position="55% 50%"
          title="Our Rope Access Services"
          subtitle="Complete range of rope access solutions for high-rise and complex structures"
          minHeight="52vh"
        />
      )}

      {current ? (
        <>
          <section className="section">
            <div className="container">
              <ServiceBlock service={current} index={0} single />
            </div>
          </section>

          <section className="section bg-light">
            <div className="container">
              <div className="section-header reveal">
                <h2>Other Rope Access Services</h2>
              </div>
              <div className="mini-links">
                {services.filter((s) => s.id !== current.id).map((s) => (
                  <Link key={s.id} to={`/services/${s.id}`} className="mini-link">
                    <span>{s.title}</span>
                    <ArrowRight size={18} aria-hidden="true" />
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      ) : (
        <section className="section">
          <div className="container">
            <div className="section-header reveal">
              <h2>Professional Rope Access Services</h2>
              <p>
                From routine cleaning to emergency repairs, we provide safe, efficient and cost-effective solutions at height. All
                work is carried out by IRATA Level 3 certified technicians.
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5rem' }}>
              {services.map((service, index) => (
                <ServiceBlock key={service.id} service={service} index={index} link />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section bg-light">
        <div className="container">
          <div className="section-header reveal">
            <h2>Our Process</h2>
            <p>How we work to deliver safe and efficient rope access solutions</p>
          </div>
          <div className="steps">
            {steps.map((item, i) => (
              <div key={item.step} className={`step reveal d${i % 4}`}>
                <div className="step-number">{item.step}</div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-dark">
        <div className="container" style={{ textAlign: 'center', color: 'var(--white)' }}>
          <h2 style={{ color: 'var(--white)', marginBottom: '1.25rem', fontSize: 'clamp(2rem, 4vw, 3.2rem)' }}>Ready to Get Started?</h2>
          <p style={{ fontSize: '1.2rem', marginBottom: '2rem', color: '#e5e9f0' }}>
            Contact us today for a free quote and site assessment
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact#quote-form" className="btn-primary">Get a Quote</Link>
            <a href="tel:0426920301" className="btn-secondary">Call Now: 0426 920 301</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
